import { Controller } from "@hotwired/stimulus"

// Drives the "save a payment method for autopay" flow (P1 #6) — a SetupIntent,
// NOT a charge. Used wherever a renter enrolls in autopay without an outstanding
// balance (the public /pay/:token page and the in-portal autopay page).
//
// On submit:
//   1. validate the Payment Element (elements.submit),
//   2. POST to clientSecretEndpoint to mint a SetupIntent on the connected
//      account (Stripe::ConnectSetupIntent) and read its client_secret,
//   3. confirmSetup() to attach + save the card/bank account (no money moves),
//   4. navigate to returnUrl, which enrolls the lease(s) server-side.
//
// `window.Stripe` is the global injected by Stripe.js; in SYSTEM TESTS it is
// STUBBED (test/support/stripe_js_stub) so there is no network call.
export default class extends Controller {
  static values = {
    publishableKey: String,
    account: String,
    clientSecretEndpoint: String,
    returnUrl: String,
  }
  static targets = ["element", "submit", "error"]

  connect() {
    if (typeof window.Stripe !== "function") {
      this.showError("Saving a card is temporarily unavailable. Please try again later.")
      return
    }

    this.stripe = window.Stripe(
      this.publishableKeyValue,
      this.accountValue ? { stripeAccount: this.accountValue } : undefined
    )
    this.elements = this.stripe.elements({ mode: "setup", currency: "usd", paymentMethodTypes: ["card", "us_bank_account"] })
    this.paymentElement = this.elements.create("payment")
    this.paymentElement.mount(this.elementTarget)
  }

  async submit(event) {
    event.preventDefault()
    if (!this.stripe) return
    this.setBusy(true)
    this.clearError()

    try {
      const { error: submitError } = await this.elements.submit()
      if (submitError) {
        this.showError(submitError.message)
        this.setBusy(false)
        return
      }

      const clientSecret = await this.fetchClientSecret()
      if (!clientSecret) {
        this.showError("We couldn't start saving your card. Please try again.")
        this.setBusy(false)
        return
      }

      const { error, setupIntent } = await this.stripe.confirmSetup({
        elements: this.elements,
        clientSecret,
        confirmParams: { return_url: this.returnUrlValue },
        redirect: "if_required",
      })

      if (error) {
        this.showError(error.message)
        this.setBusy(false)
        return
      }

      // No redirect was required (e.g. a card): finish the enrollment ourselves.
      const id = setupIntent && setupIntent.id ? setupIntent.id : ""
      window.location = this.appendParam(this.returnUrlValue, "setup_intent", id)
    } catch (e) {
      // Same silent catch-all as stripe_payment_controller, and blind in the same
      // way — a total failure here would stop every tenant saving a card for autopay
      // and produce no signal at all. Report before apologising.
      this.report(e, "setup")
      this.showError("Something went wrong. Please try again.")
      this.setBusy(false)
    }
  }

  // Fire-and-forget; never awaited, always caught. See PaymentClientErrorsController.
  report(error, stage) {
    try {
      const body = new URLSearchParams({
        page: this.element.dataset.paymentPage || "unknown",
        path: window.location.pathname,
        stage: stage || "",
        message: String((error && error.message) || error || "unknown error").slice(0, 300),
      })
      fetch("/payment-errors", { method: "POST", body, keepalive: true }).catch(() => {})
    } catch (_) {
      // Telemetry is never worth a second exception on the payment path.
    }
  }

  async fetchClientSecret() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(this.clientSecretEndpointValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token || "", Accept: "application/json" },
    })
    if (!response.ok) return null
    const data = await response.json()
    return data.client_secret
  }

  appendParam(url, key, value) {
    const sep = url.includes("?") ? "&" : "?"
    return `${url}${sep}${key}=${encodeURIComponent(value)}`
  }

  setBusy(busy) {
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = busy
      this.submitTarget.dataset.busy = busy ? "true" : "false"
    }
  }

  clearError() {
    if (this.hasErrorTarget) this.errorTarget.textContent = ""
  }

  showError(message) {
    if (this.hasErrorTarget) this.errorTarget.textContent = message
  }
}
